/**
 * "Why this agent?" on a run (G11 / V31): the routing explanation, recomputed on this device from
 * the decrypted rules and history (the hub never sees them), for the agent and machine the run
 * actually got.
 */
import { decideRouting, explainRouting } from '@viko/scheduler'
import { useMemo } from 'react'
import { useRoutingOutcomes, useRoutingPreferences } from '../lib/routing-prefs.js'
import type { RunSummary } from '../lib/types.js'

export function whyThisAgent(
  run: Pick<RunSummary, 'agent' | 'machineId' | 'title' | 'taskTitle' | 'prompt' | 'projectId'>,
  machineName: string | undefined,
  cfg: Parameters<typeof decideRouting>[1],
  now = Date.now(),
): string | undefined {
  if (run.agent !== 'claude' && run.agent !== 'codex') return undefined
  const title = run.taskTitle ?? run.title ?? run.prompt?.slice(0, 300)
  const decision = decideRouting(
    {
      id: 'run',
      projectId: run.projectId ?? '',
      title: title ?? '',
      labels: [],
      priority: 1,
      readyAt: now,
      depsSatisfied: true,
      repo: '',
    },
    cfg,
    now,
  )
  return explainRouting(decision, {
    agent: run.agent,
    machine: machineName ?? run.machineId,
    machineId: run.machineId,
  })
}

export function WhyThisAgent({ run, machineName }: { run: RunSummary; machineName?: string }) {
  const prefs = useRoutingPreferences()
  const outcomes = useRoutingOutcomes()
  const text = useMemo(
    () => whyThisAgent(run, machineName, { prefs: prefs.data, outcomes: outcomes.data }),
    [run, machineName, prefs.data, outcomes.data],
  )
  if (!text) return null
  return (
    <details className="px-4 py-1 text-[14px]" data-testid="why-this-agent">
      <summary className="min-h-11 cursor-pointer py-2 text-text-2">Why this agent?</summary>
      <p className="m-0 pb-2 text-text-2">{text}</p>
    </details>
  )
}
